import React, { useState, useRef, useCallback, useEffect } from "react";
import { motion } from "framer-motion";
import { useOnboardingAnalytics } from "@/analytics";
import {
  useUserSetProfilePhotoMutation,
  useUserRemovePhotoMutation,
} from "services/graphql";
import { Avatar } from "app/components/Avatar";
import ReactCrop, {
  Crop,
  PixelCrop,
  centerCrop,
  makeAspectCrop,
} from "react-image-crop";
import Modal from "app/components/Modal";
import { UserData } from "../../constants";
import "react-image-crop/dist/ReactCrop.css";
import styles from "./styles.module.scss";

export interface ProfilePhotoStepProps {
  onComplete: (data: Pick<UserData, "photoURL">) => void;
  initialValue?: string;
}

function centerAspectCrop(mediaWidth: number, mediaHeight: number) {
  return centerCrop(
    makeAspectCrop(
      {
        unit: "%",
        width: 90,
      },
      1,
      mediaWidth,
      mediaHeight
    ),
    mediaWidth,
    mediaHeight
  );
}

export const ProfilePhotoStep: React.FC<ProfilePhotoStepProps> = ({
  onComplete,
  initialValue,
}) => {
  const [photo, setPhoto] = useState<string>(initialValue);
  const [hasPhotoChanged, setHasPhotoChanged] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [crop, setCrop] = useState<Crop>();
  const [completedCrop, setCompletedCrop] = useState<PixelCrop>();
  const [isCropModalOpen, setIsCropModalOpen] = useState(false);
  const [tempPhoto, setTempPhoto] = useState<string>("");
  const fileInputRef = useRef<HTMLInputElement>(null);
  const imgRef = useRef<HTMLImageElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const {
    setProfilePhotoAnalytics,
    onboardingErrorAnalytics,
  } = useOnboardingAnalytics();

  useEffect(() => {
    if (containerRef.current) {
      containerRef.current.classList.add(styles.animate);
    }
  }, []);

  const [
    removePhoto,
    { loading: isRemovingPhoto },
  ] = useUserRemovePhotoMutation({
    onError: removeError => {
      onboardingErrorAnalytics(removeError.message);
      setError("Failed to remove profile picture. Please try again.");
    },
  });

  const [
    setProfilePhoto,
    { loading: isUpdatingPhoto },
  ] = useUserSetProfilePhotoMutation({
    onCompleted: data => {
      if (data?.userSetPhoto?.photoURL) {
        onComplete({ photoURL: data.userSetPhoto.photoURL });
      }
    },
    onError: uploadError => {
      onboardingErrorAnalytics(uploadError.message);
      setError("Failed to upload profile picture. Please try again.");
    },
  });

  const onImageLoad = useCallback(
    (e: React.SyntheticEvent<HTMLImageElement>) => {
      const { width, height } = e.currentTarget;
      setCrop(centerAspectCrop(width, height));
    },
    []
  );

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 4999999) {
        setError("The file you are trying to upload is too large.");
        return;
      }

      const reader = new FileReader();
      reader.onloadend = () => {
        setTempPhoto(reader.result as string);
        setError(null);
        setIsCropModalOpen(true);
      };
      reader.readAsDataURL(file);
    }
  };

  const getCroppedImg = async (
    image: HTMLImageElement,
    cropArea: PixelCrop
  ): Promise<Blob> => {
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");

    if (!ctx) {
      throw new Error("No 2d context");
    }

    const scaleX = image.naturalWidth / image.width;
    const scaleY = image.naturalHeight / image.height;

    // Make the canvas square with the crop dimensions
    const size = Math.min(cropArea.width, cropArea.height);
    canvas.width = size;
    canvas.height = size;

    ctx.drawImage(
      image,
      cropArea.x * scaleX,
      cropArea.y * scaleY,
      cropArea.width * scaleX,
      cropArea.height * scaleY,
      0,
      0,
      size,
      size
    );

    return new Promise((resolve, reject) => {
      canvas.toBlob(
        blob => {
          if (!blob) {
            reject(new Error("Canvas is empty"));
            return;
          }
          resolve(blob);
        },
        "image/jpeg",
        0.9
      );
    });
  };

  const handleAddPicture = () => {
    setProfilePhotoAnalytics("Add picture");
    fileInputRef.current?.click();
  };

  const handleSkip = () => {
    setProfilePhotoAnalytics("SKIP");
    onComplete({ photoURL: "" });
  };

  const handleCropComplete = async () => {
    if (!imgRef.current || !completedCrop) {
      setError("Please crop the photo first.");
      return;
    }

    try {
      const croppedImage = await getCroppedImg(imgRef.current, completedCrop);
      const reader = new FileReader();
      reader.onloadend = () => {
        const newPhoto = reader.result as string;
        setPhoto(newPhoto);
        setHasPhotoChanged(true);
        setIsCropModalOpen(false);
        setTempPhoto("");
        setCrop(undefined);
        setCompletedCrop(undefined);
      };
      reader.readAsDataURL(croppedImage);
    } catch (err) {
      if (err instanceof Error) {
        onboardingErrorAnalytics(err.message);
      }
      setError("Failed to process the image. Please try again.");
    }
  };

  const handleCropCancel = () => {
    setIsCropModalOpen(false);
    setTempPhoto("");
    setCrop(undefined);
    setCompletedCrop(undefined);
  };

  const handleContinue = async () => {
    try {
      setProfilePhotoAnalytics("Done");

      // If photo was explicitly removed, complete with null
      if (hasPhotoChanged && !photo) {
        onComplete({ photoURL: null });
        return;
      }

      // If no photo and not explicitly removed, show error
      if (!photo) {
        setError("Please select a photo first.");
        return;
      }

      // Only call setProfilePhoto if the photo has actually changed
      if (hasPhotoChanged) {
        const response = await fetch(photo);
        const blob = await response.blob();
        setProfilePhoto({
          variables: {
            photoFile: blob,
          },
        });
      } else {
        // If photo hasn't changed, just complete the step with the existing photo
        onComplete({ photoURL: photo });
      }
    } catch (err) {
      if (err instanceof Error) {
        onboardingErrorAnalytics(err.message);
      }
      setError("Failed to process the image. Please try again.");
    }
  };

  const handleRemove = async () => {
    try {
      setProfilePhotoAnalytics("Remove picture");
      setPhoto("");
      setError(null);
      setCrop(undefined);
      setCompletedCrop(undefined);
      setHasPhotoChanged(true);

      await removePhoto();
    } catch (err) {
      if (err instanceof Error) {
        onboardingErrorAnalytics(err.message);
        setError("Failed to remove profile picture. Please try again.");
      }
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, ease: "easeOut" }}
    >
      <h1 className={styles.title}>Add a profile picture</h1>
      <p className={styles.subtitle}>
        Add a profile picture so your friends know it&apos;s you. Everyone will
        be able to see your picture.
      </p>

      <motion.div
        className={styles.profilePicture}
        whileHover={{ scale: 1.05 }}
        transition={{ duration: 0.2 }}
      >
        {photo ? (
          <div className={styles.preview}>
            <img src={photo} alt="Profile" className={styles.previewImage} />
            <button
              type="button"
              className={styles.previewRemoveButton}
              onClick={handleRemove}
            >
              Remove
            </button>
          </div>
        ) : (
          <Avatar
            alt="Avatar"
            ref={fileInputRef}
            src={hasPhotoChanged ? null : initialValue}
          />
        )}
      </motion.div>

      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileChange}
        accept="image/*"
        style={{ display: "none" }}
      />

      {error && (
        <motion.p
          className={styles.error}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          {error}
        </motion.p>
      )}

      {photo ? (
        <motion.div
          key="photo-actions"
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -10 }}
        >
          <button
            type="button"
            className={styles.button}
            onClick={handleContinue}
            disabled={isUpdatingPhoto}
          >
            {isUpdatingPhoto ? "UPLOADING..." : "DONE"}
          </button>
          <button
            type="button"
            className={styles.secondaryButton}
            onClick={handleAddPicture}
            disabled={isUpdatingPhoto}
          >
            CHANGE PICTURE
          </button>
          <button
            type="button"
            className={styles.removeButton}
            onClick={handleRemove}
            disabled={isUpdatingPhoto || isRemovingPhoto}
          >
            {isRemovingPhoto ? "REMOVING..." : "REMOVE PICTURE"}
          </button>
        </motion.div>
      ) : (
        <motion.div
          key="no-photo-actions"
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -10 }}
        >
          <button
            type="button"
            className={styles.button}
            onClick={handleAddPicture}
          >
            ADD PICTURE
          </button>
          <button
            type="button"
            className={styles.secondaryButton}
            onClick={handleSkip}
          >
            SKIP
          </button>
        </motion.div>
      )}

      <Modal
        isOpen={isCropModalOpen}
        onBackgroundClick={handleCropCancel}
        onEscapeKeydown={handleCropCancel}
        className={styles.cropModal}
      >
        <motion.div
          className={styles.cropModalContent}
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          transition={{ duration: 0.2 }}
        >
          <h2>Crop Your Photo</h2>
          <div className={styles.cropContainer}>
            {tempPhoto && (
              <ReactCrop
                crop={crop}
                onChange={(_, percentCrop) => setCrop(percentCrop)}
                onComplete={c => setCompletedCrop(c)}
                aspect={1}
                circularCrop
              >
                <img
                  ref={imgRef}
                  src={tempPhoto}
                  onLoad={onImageLoad}
                  alt="Crop me"
                  className={styles.cropImage}
                />
              </ReactCrop>
            )}
          </div>
          <div className={styles.cropModalActions}>
            <button
              type="button"
              className={styles.secondaryButton}
              onClick={handleCropCancel}
            >
              CANCEL
            </button>
            <button
              type="button"
              className={styles.button}
              onClick={handleCropComplete}
            >
              APPLY
            </button>
          </div>
        </motion.div>
      </Modal>
    </motion.div>
  );
};
